import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Button } from '@opendate-app/setlist';
import { toastSuccess, toastError, errorText } from '../../../shared/toastHelper';

const axios = require('axios').default;

const formatDate = (value) => {
  if (!value) return '—';
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatPublishedAt = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  return `${Math.round(bytes / 1024)} KB`;
};

const ToastFileDeliveryPanel = ({ team, csrfToken }) => {
  const [summary, setSummary] = useState(null);
  const [importing, setImporting] = useState({});

  const load = useCallback(() => {
    if (!team?.id) return;

    axios.get(`/teams/${team.id}/reporting/food-and-beverage/toast-sales/file_delivery.json`)
      .then((res) => setSummary(res.data || null))
      .catch((error) => console.error('Failed to load Toast file delivery status', error));
  }, [team?.id]);

  useEffect(load, [load]);

  const importVenue = useCallback((venue) => {
    setImporting((prev) => ({ ...prev, [venue.venue_ownership_id]: true }));

    axios.post(
      `/teams/${team.id}/toast_file_imports`,
      { venue_ownership_id: venue.venue_ownership_id },
      { headers: csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {} }
    )
      .then((res) => {
        const count = (res.data?.imported_date_paths || []).length;
        toastSuccess(count > 0
          ? `Importing ${count} Toast ${count === 1 ? 'file' : 'files'} for ${venue.venue_name}.`
          : `No missing Toast files for ${venue.venue_name}.`);
        setTimeout(load, 4000);
      })
      .catch((error) => toastError(errorText(error, 'Could not start the Toast import.')))
      .finally(() => setImporting((prev) => ({ ...prev, [venue.venue_ownership_id]: false })));
  }, [team?.id, csrfToken, load]);

  if (!summary) return null;

  const venuesPending = (summary.venues || []).filter((venue) => venue.pending.length > 0);
  const venuesUnreachable = (summary.venues || []).filter((venue) => !venue.available);

  if (venuesPending.length === 0 && venuesUnreachable.length === 0) return null;

  return (
    <div className="mb-3">
      {venuesUnreachable.length > 0 && (
        <div className="mb-2">
          <Alert
            color="error"
            title="Could not reach Toast"
            description={`We could not list files for ${venuesUnreachable.map((v) => v.venue_name).join(', ')}. Sales for these venues may be incomplete.`}
          />
        </div>
      )}

      {venuesPending.length > 0 && (
        <Alert
          color="warning"
          title={`${summary.pending_count} Toast ${summary.pending_count === 1 ? 'file has' : 'files have'} not been imported`}
          description="Toast published these days but Opendate has not pulled them in yet, so their sales are missing from your P&Ls."
        />
      )}

      {venuesPending.map((venue) => (
        <div key={venue.venue_ownership_id} className="mt-2 p-3 rounded" style={{ border: '1px solid var(--sl-border-primary)' }}>
          <div className="d-flex align-items-center justify-content-between mb-2" style={{ gap: 12 }}>
            <div>
              <div className="font-weight-bold">{venue.venue_name}</div>
              <div className="small text-muted">
                Last file published {formatDate(venue.last_published_date)} · last imported {formatDate(venue.last_imported_date)}
              </div>
            </div>
            <Button
              variant="secondary"
              onClick={() => importVenue(venue)}
              disabled={importing[venue.venue_ownership_id]}
            >
              {importing[venue.venue_ownership_id] ? 'Starting…' : `Import ${venue.pending.length}`}
            </Button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="table table-sm mb-0" style={{ minWidth: 420 }}>
              <thead>
                <tr>
                  <th scope="col">Business date</th>
                  <th scope="col" className="text-right">Size</th>
                  <th scope="col" className="text-right">Published by Toast</th>
                </tr>
              </thead>
              <tbody>
                {venue.pending.map((entry) => (
                  <tr key={entry.date_path}>
                    <td>{formatDate(entry.business_date)}</td>
                    <td className="text-right">{formatSize(entry.size)}</td>
                    <td className="text-right">{formatPublishedAt(entry.published_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {venue.empty_exports.length > 0 && (
            <div className="small text-muted mt-2">
              {venue.empty_exports.length} other {venue.empty_exports.length === 1 ? 'day' : 'days'} in
              this window had an empty Toast export, so there are no sales to import for
              {' '}{venue.empty_exports.map((entry) => formatDate(entry.business_date)).join(', ')}.
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ToastFileDeliveryPanel;
